import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import Header from "../components/Header";
import { api, extractApiErrorMessage } from "../services/apiClient";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const hasToken = useMemo(() => token.trim().length > 0, [token]);

  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post(
        "/auth/forgot-password",
        { email },
        { auth: false },
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          extractApiErrorMessage(data, "No se pudo procesar la solicitud"),
        );
      }

      toast.success(
        data.message ||
          "Si el correo existe, enviaremos instrucciones para restablecer tu contraseña.",
      );
      setEmail("");
    } catch (error) {
      toast.error(error.message || "No se pudo procesar la solicitud");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      toast.error("La nueva contraseña debe tener al menos 6 caracteres");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }

    setLoading(true);

    try {
      const response = await api.post(
        "/auth/reset-password",
        { token, new_password: newPassword },
        { auth: false },
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          extractApiErrorMessage(data, "No se pudo restablecer la contraseña"),
        );
      }

      toast.success(data.message || "Contraseña restablecida exitosamente");
      navigate("/login");
    } catch (error) {
      toast.error(error.message || "No se pudo restablecer la contraseña");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-80px)]">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-center p-8 md:p-12"
        >
          <div className="w-full max-w-md">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver a iniciar sesión
            </Link>

            <h1
              className="text-4xl md:text-5xl font-bold mb-4 tracking-tight"
              style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
            >
              {hasToken ? "Nueva Contraseña" : "Recuperar Contraseña"}
            </h1>
            <p className="text-muted-foreground mb-8">
              {hasToken
                ? "Ingresa tu nueva contraseña para recuperar el acceso a tu cuenta."
                : "Te enviaremos un enlace para restablecer tu contraseña."}
            </p>

            {!hasToken ? (
              <form onSubmit={handleRequestReset} className="space-y-6">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Correo Electrónico
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="tu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Enviando..." : "Enviar enlace de recuperación"}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-6">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Nueva Contraseña
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10"
                      minLength={6}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Confirmar Contraseña
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      minLength={6}
                      required
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Restableciendo..." : "Restablecer contraseña"}
                </Button>
              </form>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="hidden lg:block relative overflow-hidden"
        >
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "url(https://images.pexels.com/photos/7578860/pexels-photo-7578860.jpeg)",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/80 to-secondary/80" />
          </div>
          <div className="relative z-10 flex items-center justify-center h-full p-12">
            <div className="text-center text-primary-foreground">
              <h2
                className="text-5xl font-bold mb-4 tracking-tight"
                style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
              >
                Seguridad de Cuenta
              </h2>
              <p className="text-xl opacity-90">
                Recupera tu acceso en pocos pasos
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPassword;
